import { Suspense } from 'react';

import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { DrawerProvider } from '@cast/design-system';
import { AccessControlProvider } from '@cast/infrastructure-abilities';
import type { Subjects } from '@cast/shared-ability-data-access';

import { SuspenseLoader } from 'components/loaders';
import { ProtectedRoute } from 'components/router';
import { BASE_PATHS } from '@oneclick/constants/navigation';
import {
  flattenNavItems,
  renderConfigRoutes,
  type OneClickLinkItem,
} from '@oneclick/routing';

import { SSO_ROUTE_CONFIG } from './ssoRouteConfig';

const SsoRoutes = () => (
  <AccessControlProvider>
    <DrawerProvider>
      <Outlet />
    </DrawerProvider>
  </AccessControlProvider>
);

const ssoPages = flattenNavItems(SSO_ROUTE_CONFIG);

const nestedSubject = (page: OneClickLinkItem): Subjects | undefined =>
  page.rules && page.rules.length > 1
    ? page.rules[page.rules.length - 1][1]
    : undefined;

export const SsoController = () => {
  const plain = ssoPages.filter((p) => !nestedSubject(p));
  const nested = ssoPages.filter((p) => nestedSubject(p));

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <Routes>
        <Route element={<SsoRoutes />}>
          <Route
            element={<ProtectedRoute subject="OrganizationSsoConnections" />}
          >
            {renderConfigRoutes({ pages: plain, suspense: false })}
            {nested.map((page) => {
              const subject = nestedSubject(page)!;
              return (
                <Route
                  key={`${page.route}-guard-${subject}`}
                  element={<ProtectedRoute subject={subject} />}
                >
                  {renderConfigRoutes({
                    pages: [page],
                    suspense: false,
                  })}
                </Route>
              );
            })}
          </Route>
        </Route>
        <Route
          path="*"
          element={<Navigate to={BASE_PATHS.ssoConnections} replace />}
        />
      </Routes>
    </Suspense>
  );
};
